import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { categories } from '../mock';
import { projectsAPI } from '../utils/api';
import { toast } from '../hooks/use-toast';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await projectsAPI.getAll(selectedCategory);
        setProjects(response.data.projects);
      } catch (error) {
        // Error handling - consider using error logging service in production
        toast({
          title: "Error",
          description: "Failed to load projects. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]); // Only re-fetch when category changes

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="pt-32 pb-16 px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-8">
            Our Work
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            A collection of our most impactful architectural designs across commercial, corporate, institutional, and transportation sectors.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="pb-12 px-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2.5 text-sm font-normal tracking-wide transition-all rounded-none border ${
                  selectedCategory === category.id
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">Loading projects...</p>
            </div>
          ) : (
            <>
              <div className="space-y-16">
                {projects.map((project) => (
                  <Link key={project.id} to={`/projects/${project.id}`}>
                    <div className="group cursor-pointer">
                      <div className="relative h-[60vh] overflow-hidden mb-6">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="max-w-2xl">
                        <p className="text-sm uppercase tracking-wider text-gray-500 mb-3">
                          {project.category}
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 group-hover:text-gray-600 transition-colors">
                          {project.title}
                        </h2>
                        <p className="text-lg text-gray-600 mb-2">{project.client}</p>
                        <p className="text-base text-gray-500">{project.location} • {project.year}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {projects.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg">No projects found in this category.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;